/* eslint-disable no-unused-vars */

// Load pptxgenjs from CDN
const loadPptxGen = () => new Promise((resolve, reject) => {
  if (window.PptxGenJS) { resolve(window.PptxGenJS); return; }
  // Remove any existing failed script
  const existing = document.querySelector('script[data-pptx]');
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.setAttribute('data-pptx', 'true');
  script.src = 'https://cdn.jsdelivr.net/npm/pptxgenjs@3.12.0/dist/pptxgen.bundle.js';
  script.onload = () => resolve(window.PptxGenJS);
  script.onerror = () => reject(new Error('Failed to load pptxgenjs'));
  document.head.appendChild(script);
});

// Load jsPDF from CDN
const loadJsPDF = () => new Promise((resolve, reject) => {
  if (window.jspdf?.jsPDF) { resolve(window.jspdf.jsPDF); return; }
  const existing = document.querySelector('script[data-jspdf]');
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.setAttribute('data-jspdf', 'true');
  script.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
  script.onload = () => resolve(window.jspdf.jsPDF);
  script.onerror = () => reject(new Error('Failed to load jsPDF'));
  document.head.appendChild(script);
});

export const generatePptx = async (data) => {
  if (!data || !data.title) throw new Error('No presentation data');
  const PptxGenJS = await loadPptxGen();
  const prs = new PptxGenJS();

  prs.layout = 'LAYOUT_WIDE';
  prs.author = 'Melius AI';
  prs.title = data.title;

  const GOLD = 'C9A84C';
  const INK = '0E0E0E';
  const PAPER = 'FAFAF8';
  const MUTED = '787878';

  // Title slide
  const titleSlide = prs.addSlide();
  titleSlide.background = { color: INK };
  titleSlide.addShape(prs.ShapeType.rect, {
    x: 0.5, y: 3.2, w: 1.5, h: 0.04,
    fill: { color: GOLD }, line: { type: 'none' },
  });
  titleSlide.addText(data.title, {
    x: 0.5, y: 1.8, w: 8.5, h: 1.2,
    fontSize: 44, fontFace: 'Georgia', color: PAPER, italic: true,
  });
  if (data.subtitle) {
    titleSlide.addText(data.subtitle, {
      x: 0.5, y: 3.4, w: 7, h: 0.6,
      fontSize: 18, fontFace: 'Calibri', color: MUTED,
    });
  }
  titleSlide.addText('Generated by Melius AI', {
    x: 0.5, y: 6.8, w: 4, h: 0.3,
    fontSize: 10, fontFace: 'Calibri', color: GOLD, italic: true,
  });

  // Content slides
  (data.slides || []).forEach((slide, idx) => {
    const s = prs.addSlide();
    s.background = { color: PAPER };
    s.addShape(prs.ShapeType.ellipse, {
      x: 9.0, y: 0.2, w: 0.25, h: 0.25,
      fill: { color: GOLD }, line: { type: 'none' },
    });
    s.addText(slide.title || `Slide ${idx + 2}`, {
      x: 0.5, y: 0.35, w: 8.3, h: 0.7,
      fontSize: 28, fontFace: 'Georgia', color: INK, italic: true,
    });
    s.addShape(prs.ShapeType.rect, {
      x: 0.5, y: 1.05, w: 9.0, h: 0.025,
      fill: { color: 'E8E6E0' }, line: { type: 'none' },
    });

    if (slide.points && slide.points.length > 0) {
      slide.points.forEach((point, pi) => {
        if (pi > 6) return; // max 7 points
        s.addShape(prs.ShapeType.ellipse, {
          x: 0.5, y: 1.38 + (pi * 0.72), w: 0.12, h: 0.12,
          fill: { color: GOLD }, line: { type: 'none' },
        });
        s.addText(point, {
          x: 0.75, y: 1.28 + (pi * 0.72), w: 8.5, h: 0.6,
          fontSize: 15, fontFace: 'Calibri', color: '3A3A3A', valign: 'middle',
        });
      });
    } else if (slide.content) {
      s.addText(slide.content, {
        x: 0.5, y: 1.25, w: 9.0, h: 4.5,
        fontSize: 16, fontFace: 'Calibri', color: '3A3A3A', valign: 'top', wrap: true,
      });
    }

    s.addText(`${idx + 2}`, {
      x: 8.8, y: 6.8, w: 0.5, h: 0.25,
      fontSize: 10, fontFace: 'Calibri', color: MUTED, align: 'right',
    });
  });

  // End slide
  const endSlide = prs.addSlide();
  endSlide.background = { color: INK };
  endSlide.addText('Thank you.', {
    x: 0.5, y: 2.8, w: 9, h: 1.2,
    fontSize: 48, fontFace: 'Georgia', color: PAPER, italic: true, align: 'center',
  });
  endSlide.addShape(prs.ShapeType.rect, {
    x: 3.5, y: 3.9, w: 3, h: 0.04,
    fill: { color: GOLD }, line: { type: 'none' },
  });
  endSlide.addText('Generated by Melius AI', {
    x: 0.5, y: 6.8, w: 9, h: 0.3,
    fontSize: 10, fontFace: 'Calibri', color: GOLD, italic: true, align: 'center',
  });

  const filename = `${data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pptx`;
  await prs.writeFile({ fileName: filename });
  return filename;
};

export const generatePdf = async (data) => {
  if (!data || !data.title) throw new Error('No document data');
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const GOLD = [201, 168, 76];
  const INK = [14, 14, 14];
  const MUTED = [120, 120, 120];
  const SOFT = [58, 58, 58];
  const W = 210;
  const MARGIN = 22;
  let y = 0;

  const addPage = () => { doc.addPage(); y = MARGIN; };
  const checkY = (needed = 20) => { if (y + needed > 270) addPage(); };

  // Title page
  doc.setFillColor(...INK);
  doc.rect(0, 0, W, 297, 'F');
  doc.setFillColor(...GOLD);
  doc.rect(MARGIN, 115, 45, 1.2, 'F');
  doc.setTextColor(250, 250, 248);
  doc.setFont('times', 'italic');
  doc.setFontSize(38);
  doc.text(data.title, MARGIN, 100);
  if (data.subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    doc.setTextColor(...MUTED);
    doc.text(data.subtitle, MARGIN, 124);
  }
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...GOLD);
  doc.text('Generated by Melius AI', MARGIN, 282);

  // Content sections
  const sections = data.sections || data.slides || [];
  sections.forEach((section, idx) => {
    addPage();

    // Light background
    doc.setFillColor(250, 250, 248);
    doc.rect(0, 0, W, 297, 'F');

    // Gold top accent
    doc.setFillColor(...GOLD);
    doc.rect(MARGIN, y, W - (MARGIN * 2), 0.8, 'F');
    y += 8;

    // Section title
    doc.setFont('times', 'italic');
    doc.setFontSize(24);
    doc.setTextColor(...INK);
    doc.text(section.title || `Section ${idx + 1}`, MARGIN, y);
    y += 10;

    // Thin divider
    doc.setDrawColor(220, 218, 212);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y, W - MARGIN, y);
    y += 10;

    // Content — full paragraphs
    if (section.content) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(...SOFT);
      const lines = doc.splitTextToSize(section.content, W - MARGIN * 2);
      lines.forEach(line => {
        checkY(8);
        doc.text(line, MARGIN, y);
        y += 7;
      });
      y += 4;
    }

    // Points as paragraphs not bullets
    if (section.points && section.points.length > 0) {
      section.points.forEach(point => {
        checkY(14);
        // Gold dot
        doc.setFillColor(...GOLD);
        doc.circle(MARGIN + 2, y - 2, 1.5, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(...SOFT);
        const lines = doc.splitTextToSize(point, W - MARGIN * 2 - 10);
        lines.forEach((line, li) => {
          checkY(8);
          doc.text(line, MARGIN + 8, y + (li * 7));
        });
        y += (lines.length * 7) + 5;
      });
    }

    // Page number
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(`${doc.internal.getNumberOfPages()}`, W - MARGIN, 287, { align: 'right' });
  });

  const filename = `${data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
  doc.save(filename);
  return filename;
};